import { createRef, FC, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ModalComponent from "src/core/components/modal/modal.component";
import FormBuilderComponent, {
  FormConfig,
} from "src/shared/form-builder/form-builder.component";
import { safeAtob } from "src/shared/helpers/enode-decode-string";
import { addEditFloorWithBlockCode } from "src/store/slices/dashboard.slice";
import * as yup from "yup";
import "./canvas/floorPlan.scss";
import DefaultList from "./dashboard-floorplan-defaultlist";
import DashboardFloorplanWildcardSearchingComponent from "./dashboard-floorplan-wildcard-searching.component";
import HasPermission from "src/core/guards/has-permission";
import { B2Button } from "@hdb/biome-2";

interface DashboardFloorplanEmptyFloorPageProps {
  resetComponent: () => void;
}

const DashboardFloorplanEmptyFloorPage: FC<
  DashboardFloorplanEmptyFloorPageProps
> = ({ resetComponent }) => {
  const params = useParams();
  const formRef = createRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);

  const query = queryParams.get("update");
  const [addLevelModal, setAddLevelModal] = useState(false);

  const blockCode = params.blockCode ? safeAtob(params.blockCode) : null;

  const [selectedAssetCodeForShape, setSelectedAssetCodeForShape] =
    useState("");

  const formRefFloor = createRef();

  const formConfigFloor: FormConfig = {
    controls: [
      {
        name: "type",
        label: "",
        placeholder: "Select Floor Type",
        type: "select",
        options: [],
      },
    ],
  };

  const validationSchemaFloor = yup.object().shape({});

  const navigateToUrl = (url: any, isReplaced: boolean = false) => {
    navigate(url, { replace: isReplaced });
  };

  const [initialValuesFloor, setInitialValuesFloor] = useState<any>({
    type: "2",
  });

  const onSubmitLevelModal = () => {
    const form: any = formRef.current;

    if (form) {
      form.handleSubmit();
      const { blockCode, FloorCode } = form.values;

      if (!blockCode || !FloorCode) return;

      dispatch(addEditFloorWithBlockCode({ blockCode, Id: 0, FloorCode })).then(
        (res: any) => {
          setAddLevelModal(false);

          if (res.payload.status === 201) {
            toast.success(res.payload.message, {
              position: "top-right",
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: false,
              pauseOnFocusLoss: true,
              draggable: true,
              pauseOnHover: true,
            });
          }

          resetComponent();
        }
      );
    }
  };

  const addLevelModalActionConfig: any = [
    {
      label: "Save",
      event: onSubmitLevelModal,
      variant: "primary",
    },
  ];

  const [initialValues, setInitialValues] = useState<any>({
    blockCode: blockCode,
    FloorCode: "",
  });

  const validationSchema = yup.object().shape({
    FloorCode: yup.string().required("FloorCode is required"),
  });

  const formConfig: FormConfig = {
    controls: [
      {
        name: "blockCode",
        label: "Block Code",
        placeholder: "",
        type: "text",
        required: true,
        readOnly: true,
        disabled: true,
      },
      {
        name: "FloorCode",
        label: "Floor",
        placeholder: "",
        type: "text",
        required: true,
      },
    ],
  };

  return (
    <>
      <div className="d-flex justify-content-between">
        <div className="w-lg-25 z-1 position-relative">
          <div className="mb-2">
            <HasPermission
              moduleName="Floor Plan Detail"
              actionName="Update-Floor Plan Detail"
              isRoute={false}
            >
              {/* <img
                className="cursor-pointer ms-2 mb-2"
                src={addGroups}
                alt="addLevel"
                title="Add Level"
                onClick={() => setAddLevelModal?.(true)} // Optional chaining for function
                width={28}
                height={29}
              /> */}
              <B2Button
                variant="primary"
                className="cursor-pointer w-100"
                onClick={() => setAddLevelModal?.(true)} // Optional chaining for function
              >
                Add Level
              </B2Button>
            </HasPermission>
          </div>

          <DefaultList
            dataItems={[]}
            isLoadingFloors={false}
            hasMore={false}
            fetchMoreData={() => {}}
            navigateToUrl={navigateToUrl}
          />
        </div>

        <div
          className="w-lg-75 bg-card shadow-sm z-1"
          style={{ marginLeft: "1rem" }}
        >
          <div className="flex flex-col">
              {!query && (
                <div className="d-flex justify-content-between">
                  <div
                    style={{
                      position: "relative",
                      marginTop: "20px",
                    }}
                  >
                    <DashboardFloorplanWildcardSearchingComponent
                      setSelectedSearchedAssetCodeForHighlighting={
                        setSelectedAssetCodeForShape
                      }
                    />
                  </div>

                  <div style={{ width: "15rem" }}>
                    <FormBuilderComponent
                      formRef={formRefFloor}
                      initialValues={initialValuesFloor}
                      validation={validationSchemaFloor}
                      formConfig={formConfigFloor}
                    />
                  </div>
                </div>
              )}

              <div>
                <div className="mb-3">
                  {!query && (
                    <div className="w-100" id="totalWidth">
                      <div className="no-preview-container">
                        <h5>No Floor Plan to Preview</h5>
                      </div>
                    </div>
                  )}
                </div>
              </div>
          </div>
        </div>
      </div>
      {addLevelModal && (
        <ModalComponent
          headerTitle="Add Floor Level"
          showModal={addLevelModal}
          handleClose={() => setAddLevelModal(false)}
          actionConfig={addLevelModalActionConfig}
        >
          <FormBuilderComponent
            formRef={formRef}
            initialValues={initialValues}
            validation={validationSchema}
            formConfig={formConfig}
          />
        </ModalComponent>
      )}
    </>
  );
};

export default DashboardFloorplanEmptyFloorPage;
